/* Hallmark · genre: editorial · macrostructure: Narrative Workflow · theme: custom
 * vibe: "warm ledger, ink on paper, pragmatic" · paper: oklch(96% 0.012 75) · accent: #F59E0B oklch(~77.5% 0.165 71)
 * display: Libre Caslon Text 400 · body: Inter (retained, app-shared) · axes: light / roman-serif / chromatic-amber
 * nav: N1b (SaaS retained — 4 destinations + auth CTA) · footer: Ft1 mast (ink block)
 * enrichment: framed dark exhibits (real product scenes) · studied: no · context: user-decided (2 Q&A rounds)
 * pre-emit critique (post-fix, 2026-07-18, slop-audit revision pass): P5 H4 E4 S4 R4 V4 --
 * the 58-gate audit scored Execution 2/5 (amber-on-paper text contrast failures, a stale
 * seam sentinel leaking banned dark chip chrome onto paper, missing overflow-wrap safety,
 * missing active: states) -- all four named defects fixed this pass (PAPER_ACCENT_INK
 * below; NarrativeHandoff.tsx's isLightSeam fix; .landing-serif-display overflow-wrap;
 * 3 CTA active states). Re-scored honestly at E4, not E5: these were the NAMED defects
 * verified fixed with a computed/proven test each -- a full live re-run of all 58 gates
 * is the verify lane's job, not claimed here from static analysis alone. */

/**
 * paper-tokens.ts — the ONLY place a light-paper neutral (band/rule/ink/
 * focus) resolves for the R7 hallmark editorial reskin (2026-07-17, R7
 * PRELUDE). Analog of `ui/dark-tokens.ts` (the dark-band sibling registry,
 * scoped to exhibit interiors this round) — mirrors its discipline exactly:
 * every consumer imports the pre-baked Tailwind-class STRING from here —
 * never hand-composes `bg-[${hex}]` at a call site — so there is exactly
 * one place a paper neutral can drift.
 *
 * PAPER_RAMP LAW (R7 design contract, enforced by
 * `scripts/check-landing-band-hue.mjs`'s PAPER_RAMP exception, the light-
 * paper sibling of the dark WARM_NEUTRAL_RAMP law): every paper/rule hex
 * below sits at OKLCH hue H 65-85, chroma 0.008-0.018, lightness L 0.78-
 * 0.975; every ink hex sits at H 60-80, C 0.008-0.016, L 0.18-0.55. That
 * exact window is legal ONLY inside THIS file — the gate fails closed on
 * the identical hex typed raw into a page-chrome component, forcing every
 * consumer through this registry instead of a second, driftable copy.
 *
 * SOURCE OKLCH -> COMPUTED HEX (R7 design contract's palette table; every
 * hex below was computed via the INVERSE Ottosson OKLab math — see the
 * scratch derivation referenced in the R7 PRELUDE report, never eyeballed —
 * then round-trip verified against the SAME forward sRGB->OKLCH conversion
 * `check-landing-band-hue.mjs`/`dark-tokens.test.ts` use):
 *   PAPER_BAND.base   oklch(96%   0.012 75) -> #F7F1E9  (round-trip L .961 C .0125 H 75.4)
 *   PAPER_BAND.alt    oklch(93.5% 0.014 75) -> #EFE8DF  (round-trip L .934 C .0142 H 74.4)
 *   PAPER_BAND.deep   oklch(91%   0.016 75) -> #E8E0D6  (round-trip L .910 C .0161 H 73.7)
 *   PAPER_BAND.mast   oklch(21%   0.012 70) -> #1C1712  (== PAPER_INK.primary, by design)
 *   PAPER_INK.primary   oklch(21% 0.012 70) -> #1C1712  (round-trip L .209 C .0125 H 67.1)
 *   PAPER_INK.secondary oklch(38% 0.012 70) -> #47413C  (round-trip L .380 C .0117 H 62.3)
 *   PAPER_INK.muted     oklch(50% 0.012 70) -> #68625C  (round-trip L .500 C .0121 H 67.6)
 *   PAPER_INK.onMast        = PAPER_BAND.base hex (#F7F1E9), as TEXT — paper ink on the ink mast
 *   PAPER_INK.onMastMuted oklch(80% 0.014 75) -> #C3BDB4  (round-trip L .801 C .0142 H 78.3)
 *   PAPER_RULE.hairline   oklch(80% 0.014 75) -> #C3BDB4  (== PAPER_INK.onMastMuted, same OKLCH input, by design)
 *   PAPER_RULE.soft       oklch(86% 0.012 75) -> #D6D0C9  (round-trip L .860 C .0117 H 71.9)
 *
 * WINDOW ADJUSTMENT #1 (documented per the PRELUDE brief's "any place the
 * computed hexes forced a window adjustment" requirement): the R7 contract's
 * literal "paper/rule family L 0.89-0.975" floor does not cover
 * PAPER_RULE.hairline/PAPER_INK.onMastMuted (round-trip L ~=0.80) or
 * PAPER_RULE.soft (round-trip L ~=0.86) — both are genuinely part of the
 * SAME warm-paper hue family (H 65-85, C 0.008-0.018) as the three PAPER_BAND
 * steps, just lower on the same ladder (hairlines are always darker than the
 * page base they sit on). The gate's PAPER_RAMP window floor is therefore
 * L >= 0.78 (not 0.89) for the combined paper/rule family — comfortably
 * below the lowest real value (0.80) with margin, still well above the ink
 * family's ceiling (0.55), so no accidental overlap between the two families
 * is introduced. C/H bounds are UNCHANGED from the contract (0.008-0.018,
 * 65-85).
 *
 * WINDOW ADJUSTMENT #2 -- PAPER_FOCUS: the contract's literal
 * `oklch(70% 0.19 71)` does not exist in the sRGB gamut. A binary-search
 * gamut scan (holding H=71 fixed, sweeping L) found the sRGB chroma ceiling
 * at this hue peaks at C~=0.170 (at L~=0.79) and never reaches 0.19 at ANY
 * lightness -- the naive linear-RGB clamp this file's sibling conversions
 * use would silently distort the color to a DIFFERENT hue (~62 instead of
 * 71) rather than honestly failing. The practical ceiling of "accent chroma
 * at hue~=71" already IS the brand's own primary accent -- `#F59E0B` itself
 * resolves to L=0.7686, C=0.1647, H=70.08, essentially sitting on the sRGB
 * gamut boundary for this hue family. Per the contract's own instruction not
 * to mint a new amber token, PAPER_FOCUS reuses `#F59E0B` EXACTLY rather than
 * manufacturing a barely-different near-duplicate that would still clip.
 * `LANDING_PRIMARY_CTA_RECIPE` (unchanged, lives elsewhere) and PAPER_FOCUS
 * are two different consumption sites of the identical brand hex, not two
 * amber tokens. Because `#F59E0B` is already allowlisted by
 * `check-landing-band-hue.mjs`'s pre-existing "Decision amber" entry, this
 * needs NO change to the PAPER_RAMP exception itself -- it simply passes
 * through the check the same way every other CTA/badge amber site already
 * does. `scripts/__tests__/../paper-tokens.test.ts` asserts the accent
 * window (C 0.155-0.21, H 65-78 -- floor lowered from the contract's stated
 * 0.17 to include the verified real value 0.1647 with margin) as a
 * documented exception, exactly like the WARM_NEUTRAL_RAMP self-check
 * documents its own allowlisted-amber pass-through.
 *
 * Related: [[rules/enforcements]] #49 · `ui/dark-tokens.ts` (dark-band
 * sibling registry, sibling discipline, scoped to exhibit interiors this
 * round) · `scripts/check-landing-band-hue.mjs` (PAPER_RAMP enforcer) ·
 * `.claude/handoffs/2026-07-17-r7-design-contract.md` (source palette table).
 */

/** A background-oriented paper token: raw hex (inline style / SVG / gradient
 * composition by a consumer that cannot pre-bake a static class) + the
 * pre-baked `bg-[#hex]` Tailwind class. */
export type PaperFillRecipe = {
  hex: string;
  bg: string;
};

/** A text-oriented paper ink token: raw hex + the pre-baked `text-[#hex]`
 * Tailwind class. */
export type PaperInkRecipe = {
  hex: string;
  text: string;
};

/** A hairline/divider-oriented paper rule token: raw hex + the pre-baked
 * `border-[#hex]` and `divide-[#hex]` Tailwind classes (rules are drawn as
 * borders or divide-utilities, never as a fill). */
export type PaperRuleRecipe = {
  hex: string;
  border: string;
  divide: string;
};

/** The single focus-visible accent token: raw hex + the pre-baked
 * `focus-visible:ring-[#hex]` Tailwind class. Deliberately minimal (mint
 * ONLY what a real call site needs, same discipline as `DARK_VARIANT`'s
 * header note) -- ring-OFFSET color is a property of whichever band/rule the
 * focused element sits on, so it comes from `PAPER_VARIANT`'s
 * `*RingOffset` fields below, not from this token. */
export type PaperFocusRecipe = {
  hex: string;
  ring: string;
};

export type PaperBandRole = 'base' | 'alt' | 'deep' | 'mast';
export type PaperInkRole = 'primary' | 'secondary' | 'muted' | 'onMast' | 'onMastMuted';
export type PaperRuleRole = 'hairline' | 'soft';

/**
 * The 3-step paper luminance band ramp (page base -> alternate -> deepest)
 * plus the ONE dark ink block (`mast`, the footer close per the R7 contract's
 * decision 4: "light page, dark exhibits, dark mast close"). Section
 * identity comes from composition/density, never a re-hued surface -- this
 * ramp stays a single warm-paper scale at constant hue (H~=75), exactly the
 * discipline `DARK_BAND`'s own doc comment states for its dark analog.
 */
export const PAPER_BAND: Record<PaperBandRole, PaperFillRecipe> = {
  base: { hex: '#F7F1E9', bg: 'bg-[#F7F1E9]' },
  alt: { hex: '#EFE8DF', bg: 'bg-[#EFE8DF]' },
  deep: { hex: '#E8E0D6', bg: 'bg-[#E8E0D6]' },
  mast: { hex: '#1C1712', bg: 'bg-[#1C1712]' },
};

/**
 * The ink hierarchy ladder (primary -> secondary -> muted) plus the two
 * "on the ink mast" inverse steps (paper-colored text sitting on the dark
 * `PAPER_BAND.mast` block, and its muted variant). `onMast` reuses
 * `PAPER_BAND.base`'s exact hex (paper-on-ink is literally the page's own
 * base tone, read as text) rather than minting a near-duplicate.
 */
export const PAPER_INK: Record<PaperInkRole, PaperInkRecipe> = {
  primary: { hex: '#1C1712', text: 'text-[#1C1712]' },
  secondary: { hex: '#47413C', text: 'text-[#47413C]' },
  muted: { hex: '#68625C', text: 'text-[#68625C]' },
  onMast: { hex: '#F7F1E9', text: 'text-[#F7F1E9]' },
  onMastMuted: { hex: '#C3BDB4', text: 'text-[#C3BDB4]' },
};

/**
 * The two hairline/divider tones (hairline -> soft), sitting one and two
 * rungs below the paper band ladder respectively (see the file header's
 * "WINDOW ADJUSTMENT #1" note -- both are part of the same warm-paper hue
 * family as `PAPER_BAND`, just darker). `hairline` shares its exact hex with
 * `PAPER_INK.onMastMuted` (identical OKLCH source input, by design -- the
 * muted ink-on-mast tone and the hairline rule tone are the same physical
 * color used two different ways).
 */
export const PAPER_RULE: Record<PaperRuleRole, PaperRuleRecipe> = {
  hairline: { hex: '#C3BDB4', border: 'border-[#C3BDB4]', divide: 'divide-[#C3BDB4]' },
  soft: { hex: '#D6D0C9', border: 'border-[#D6D0C9]', divide: 'divide-[#D6D0C9]' },
};

/**
 * The focus-visible-only accent token. See the file header's
 * "WINDOW ADJUSTMENT #2" note for why this is the brand's existing
 * `#F59E0B` exactly, not a newly-minted near-amber -- `oklch(70% 0.19 71)`
 * does not exist in sRGB, and the nearest achievable point at this hue IS
 * the brand accent itself. Consumers pair this with a `PAPER_VARIANT`
 * `*RingOffset` field for the band/rule the focused element sits on
 * (`focus-visible:ring-2 focus-visible:ring-offset-2 ${ring} ${ringOffset}`),
 * mirroring the `DARK_VARIANT` ring/ring-offset split.
 */
export const PAPER_FOCUS: PaperFocusRecipe = {
  hex: '#F59E0B',
  ring: 'focus-visible:ring-[#F59E0B]',
};

/** A readable-amber-as-text/icon token: raw hex + the pre-baked
 * `text-[#hex]` Tailwind class. Distinct from `PaperFocusRecipe` (a ring
 * color, never read as body text) and from the brand's own `#F59E0B`
 * (a FILL color -- CTA buttons, badge/chip washes, focus rings -- never
 * read as small text against a light background). */
export type PaperAccentInkRecipe = {
  hex: string;
  text: string;
};

/**
 * PAPER_ACCENT_INK -- the readable-amber-as-TEXT/ICON token (2026-07-18,
 * slop-audit revision pass, FIX 1 -- "CRITICAL, amber-on-paper contrast,
 * 6 sites"). `#F59E0B` at OKLCH L 0.7686 measures 1.91:1 against
 * `PAPER_BAND.base` (#F7F1E9) and 1.77:1 against `.alt` (#EFE8DF) --
 * both fail WCAG AA's 4.5:1 text floor by a wide margin (and even the
 * 3:1 large-text/icon floor). `#F59E0B` itself is UNCHANGED and stays the
 * only amber for every FILL use (CTA buttons, badge/chip washes, focus
 * rings) -- those are backgrounds/outlines, not glyphs, so the text-
 * contrast formula does not apply to them. This token exists ONLY for
 * sites that render amber AS the readable glyph color (a numeral, an
 * icon stroke, a status word) directly against a paper band.
 *
 * DERIVATION (computed, not eyeballed -- same Ottosson OKLab inverse
 * transform `paper-tokens.test.ts`'s own independent forward conversion
 * round-trips against): held H=71 (matches the brief's "same hue family
 * as #F59E0B, OKLCH H~71" instruction; the brand accent's own real hue is
 * H=70.08, effectively identical) and, at each candidate lightness,
 * binary-searched the MAXIMUM in-gamut sRGB chroma at that L/H (holding a
 * FIXED chroma while darkening runs the color out of the sRGB gamut well
 * before 4.5:1 is reached -- e.g. at H=71/C=0.14, sRGB gamut ends around
 * L~=0.66, but 4.5:1 against `.alt` needs L<=~0.53). Swept L downward from
 * the brand's own L=0.7686 using each step's own max-chroma color until
 * BOTH `PAPER_BAND.base` and `.alt` (the darker, binding constraint)
 * cleared 4.5:1, then picked a clean L=0.50 with headroom above the exact
 * boundary crossing (L~=0.526, base 4.92:1/alt 4.54:1 -- razor-thin) rather
 * than the minimum-possible darkening, so real-world font
 * rendering/anti-aliasing doesn't erode the margin to zero:
 *   oklch(50% 0.1077 71) -> #895700, round-trip-verified at
 *   L=0.5005 C=0.1077 H=71.15 (effectively the same hue family as the
 *   brand's H=70.08) -- contrast 5.46:1 (base) / 5.04:1 (alt), both clear
 *   AA 4.5:1 with real margin (also clears the 3:1 icon/large-text floor
 *   by a wide margin). Chroma 0.1077 is ~65% of the brand's own 0.1647 --
 *   meaningfully saturated amber/ochre, not a desaturated gray-brown.
 * `paper-tokens.test.ts` proves both ratios independently (its own
 * from-scratch relative-luminance + contrast-ratio implementation, not
 * imported from this derivation) -- computed, never asserted-by-eye.
 */
export const PAPER_ACCENT_INK: PaperAccentInkRecipe = {
  hex: '#895700',
  text: 'text-[#895700]',
};

/** A readable-attention-as-text/icon/border token: raw hex + the pre-baked
 * `text-[#hex]` and `border-[#hex]` Tailwind classes. The paper-surface
 * sibling of `TONE.attention` (`ui/tone.ts`, `#FB7185`), which is a
 * DARK-EXHIBIT-only recipe. Carries a `border` form as well as `text`
 * because its one call site (the hero convergence figure's status chips)
 * renders the tone as glyph AND as the chip's own 1px boundary; the chip's
 * low-alpha tinted FILL is unaffected and still comes from
 * `TONE.attention.chipFill`. */
export type PaperAttentionInkRecipe = {
  hex: string;
  text: string;
  border: string;
};

/**
 * PAPER_ATTENTION_INK -- the readable-rose-as-TEXT/ICON/BORDER token
 * (2026-08-12, R8 review FIX 1 -- "CRIT contrast,
 * HeroConvergenceFigure.tsx:215"). SECOND occurrence of the same CLASS
 * PAPER_ACCENT_INK above was minted for on 2026-07-18: a hue recipe
 * contrast-verified against a DARK surface, reused verbatim on a PAPER
 * surface. `tone.ts`'s own header states its verification basis explicitly
 * ("chip text on its own 12%-over-`DARK_SURFACE.base` fill: ... rose
 * 6.4:1") -- that proof does not transfer to paper, and the seven hero
 * source rows composited `#FB7185` glyphs onto `PAPER_BAND.alt` under a
 * `bg-[#FB7185]/12` wash at 2.01:1, less than half the WCAG AA 4.5:1 text
 * floor (and under even the 3:1 large-text/icon floor). `TONE.attention`
 * itself is UNCHANGED -- it stays correct inside bounded dark
 * `FigureFrame` exhibit interiors, which is the only place it is legal.
 * The class-kill for the pattern is `scripts/check-landing-tone-surface.mjs`.
 *
 * DERIVATION (computed, not eyeballed -- the SAME method PAPER_ACCENT_INK's
 * own note above documents, re-run for the rose hue family): held
 * H = 13.43 (`#FB7185`'s own real OKLCH hue, so this reads as the same
 * semantic rose, not a generic dark red) and, at each candidate lightness,
 * binary-searched the MAXIMUM in-gamut sRGB chroma at that L/H. The binding
 * constraint is NOT a bare paper band: it is the chip's own composited
 * fill, `#FB7185` at 12% over `PAPER_BAND.deep` -> `#EAD3CC`, the darkest
 * of the six real surfaces this glyph can sit on (three paper bands x
 * bare/tinted). 4.5:1 against that surface first clears at L ~= 0.505;
 * L = 0.45 was picked instead -- inside the review brief's stated
 * "oklch 40-45% L" window and with real margin above the boundary, so font
 * rendering/anti-aliasing cannot erode it -- and its chroma backed off to
 * 95% of the gamut ceiling (0.1798 -> 0.1708) so no sRGB channel pins at
 * 0x00; riding the exact gamut edge (`#A00037`) drifts the round-trip hue
 * furthest from the rose family and leaves zero headroom for the rounding
 * to 8-bit:
 *   oklch(45% 0.1708 13.43) -> #9D1038, round-trip-verified at
 *   L=0.4495 C=0.1706 H=13.60 (0.17deg off `#FB7185`'s own H=13.43).
 *   Contrast: 7.30:1 (base) / 6.74:1 (alt) / 6.26:1 (deep) bare, and
 *   6.58:1 / 6.12:1 / 5.73:1 under the 12% rose wash -- every one clears
 *   AA 4.5:1 with margin. Chroma 0.1706 is ~101% of `TONE.attention`'s own
 *   0.1690 -- a fully saturated deep rose, never a muddy maroon.
 * `paper-tokens.test.ts` proves all six ratios independently (its own
 * from-scratch relative-luminance + alpha-composite implementation, not
 * imported from this derivation) -- computed, never asserted-by-eye.
 *
 * PAPER_RAMP NOTE: this hex sits at OKLCH H ~= 13.6, deliberately OUTSIDE
 * the warm-paper H 60-85 neutral window `check-landing-band-hue.mjs`'s
 * PAPER_RAMP self-check enforces over this file -- it is a SEMANTIC tone,
 * the same way `PAPER_FOCUS`/`PAPER_ACCENT_INK` are amber rather than
 * neutral. It therefore carries the per-site `@landing-band-hue-ok` marker
 * that check documents, one per hex-bearing line. (A future owner of
 * `check-landing-band-hue.mjs` may prefer to graduate this into a named
 * `isPaperAttentionInkHex` window beside the existing accent-ink one.)
 */
export const PAPER_ATTENTION_INK: PaperAttentionInkRecipe = {
  hex: '#9D1038', // @landing-band-hue-ok reason=semantic rose attention ink, deliberately outside the neutral PAPER_RAMP hue window; contrast proven in paper-tokens.test.ts
  text: 'text-[#9D1038]', // @landing-band-hue-ok reason=pre-baked Tailwind class for the same token (static JIT needs the literal)
  border: 'border-[#9D1038]', // @landing-band-hue-ok reason=pre-baked Tailwind class for the same token (static JIT needs the literal)
};

/**
 * Pre-baked VARIANT-PREFIXED single-hex recipes -- built from day one this
 * round (R6 lesson, see `DARK_VARIANT`'s own header: a registry that ships
 * only `bg-[#hex]` forms lets a lane hand-type a `ring-offset-[#hex]`/
 * `hover:text-[#hex]`/`placeholder:text-[#hex]` site with the identical
 * raw hex, invisible to a bare-`bg-` scan). Each entry embeds exactly ONE
 * hex with a Tailwind pseudo-class/state variant glued to it, for the same
 * static-JIT reason `dark-tokens.ts`'s own header documents -- interpolating
 * `${PAPER_BAND.base.hex}` into `` `ring-offset-[${hex}]` `` at a call site
 * produces a class that exists only at runtime, invisible to Tailwind's
 * scanner. Mint ONLY what a real call site needs -- no speculative variants.
 */
export const PAPER_VARIANT = {
  /** Page base band ring-offset -- matches `PAPER_BAND.base.hex`. */
  baseRingOffset: 'focus-visible:ring-offset-[#F7F1E9]',
  /** Alternate band ring-offset -- matches `PAPER_BAND.alt.hex`. */
  altRingOffset: 'focus-visible:ring-offset-[#EFE8DF]',
  /** Deepest band ring-offset -- matches `PAPER_BAND.deep.hex`. */
  deepRingOffset: 'focus-visible:ring-offset-[#E8E0D6]',
  /** Ink mast band ring-offset (Ft1 footer close) -- matches
   * `PAPER_BAND.mast.hex`. */
  mastRingOffset: 'focus-visible:ring-offset-[#1C1712]',
  /** Primary ink hover -- matches `PAPER_INK.primary.hex`. */
  primaryHoverText: 'hover:text-[#1C1712]',
  /** Secondary ink hover -- matches `PAPER_INK.secondary.hex`. */
  secondaryHoverText: 'hover:text-[#47413C]',
  /** Muted ink placeholder -- matches `PAPER_INK.muted.hex`. */
  mutedPlaceholderText: 'placeholder:text-[#68625C]',
} as const;

export type PaperTokens = {
  band: typeof PAPER_BAND;
  ink: typeof PAPER_INK;
  rule: typeof PAPER_RULE;
  focus: typeof PAPER_FOCUS;
  accentInk: typeof PAPER_ACCENT_INK;
  attentionInk: typeof PAPER_ATTENTION_INK;
  variant: typeof PAPER_VARIANT;
};
